import styled from 'styled-components';
import parseZone from 'helpers/parseZone';
import { FormProvider } from 'react-hook-form';
import useFormWithErrorTracking from 'hooks/useFormWithErrorTracking';
import { usePreApproveBookingMutation } from 'components/booking/mutationHooks';
import { useCloseModal } from 'components/remodal/v2/hooks';
import useLazySnackbar from 'components/banners/useLazySnackbar';
import Textarea from 'components/ui/Textarea/Textarea';
import ErrorMessage from 'components/ui/ErrorMessage/ErrorMessage';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import Button from 'components/ui/Button/Button';
import useModal from 'components/remodal/v2/useModal';
import useRudderStackTrack from 'hooks/useRudderStackTrack';
import { dollarsWithCentsWithSymbol } from '../../helpers/currency';
import { ownerEarnings } from '../../stacks/messaging/helpers';
import { useBreakpoint } from '../BreakpointProvider';
import { mediaMSmMax } from '../../helpers/breakpoints';
import PriceBreakdownModal from './PriceBreakdownModal';

const PreApproveModal = ({ booking, onSuccess }) => {
  const closeModal = useCloseModal();
  const { enqueueSnackbar } = useLazySnackbar();
  const breakpoints = useBreakpoint();
  const smallScreen = breakpoints.xs || breakpoints.sm || breakpoints.md;

  const preApproveBookingMutation = usePreApproveBookingMutation({
    onSuccess() {
      if (onSuccess) onSuccess();
      enqueueSnackbar('This inquiry has been pre-approved.', { variant: 'success' });
    },
    onError(error) {
      console.error(error);
    },
    onSettled() {
      closeModal();
    },
  });

  const rudderTrack = useRudderStackTrack();

  const approve = async ({ pickup_instructions }) => {
    preApproveBookingMutation.mutate({
      bookingId: booking.id,
      body: {
        pickup_instructions,
      },
    });
    rudderTrack('booking_action_owner', {
      booking: booking.id,
      action: 'pre-approve',
      location: 'pre-approve-modal',
    });
  };

  const { modal: priceBreakdownModal, openModal: openPriceBreakdownModal } = useModal(
    <PriceBreakdownModal booking={booking} />,
    {
      size: 'kindaSmall',
      trackingName: 'price_breakdown_modal',
    }
  );

  const methods = useFormWithErrorTracking({
    mode: 'onChange',
  });
  const { handleSubmit, register, errors, watch } = methods;

  const getError = () => {
    const errorsList = [];

    if (errors.p2p) errorsList.push(errors.p2p.message);
    if (errors.terms) errorsList.push(errors.terms.message);
    if (errors.uscg) errorsList.push(errors.uscg.message);

    if (errorsList.length > 0) {
      return `You must agree to the ${errorsList.join(' / ')} to proceed `;
    }

    return null;
  };

  const diffHours = parseZone(booking?.trip_finish).diff(parseZone(booking?.trip_start), 'hour', true);

  return (
    <Wrapper>
      <Heading>Pre-approve inquiry</Heading>

      <p>
        If you are okay with {booking?.renter?.first_name}&apos;s inquiry, you can pre-approve it. If they choose to
        book, their request will automatically be approved. Please note that pre-approving an inquiry does not block
        your calendar.
      </p>

      <Section>
        <SectionHeading>Details</SectionHeading>
        <ItemsList>
          <ItemTitle>Start date</ItemTitle>
          <ItemData>{parseZone(booking?.trip_start).format('MMM D, YYYY • h:mm A')}</ItemData>
          <ItemTitle>End date</ItemTitle>
          <ItemData>{parseZone(booking?.trip_finish).format('MMM D, YYYY • h:mm A')}</ItemData>
          <ItemTitle>Duration</ItemTitle>
          <ItemData>{diffHours >= 24 ? `${Math.ceil(diffHours / 24)} days` : `${diffHours} hours`}</ItemData>
          <ItemTitle>Captain</ItemTitle>
          <ItemData>{booking?.package_type === 'bareboat' ? 'No captain' : 'Captained'}</ItemData>
          <ItemTitle>Passengers</ItemTitle>
          <ItemData>
            {booking?.passengers} {booking?.passengers > 1 ? 'people' : 'person'}
          </ItemData>
        </ItemsList>
      </Section>

      <Section>
        <SectionHeading>Pricing and earnings</SectionHeading>
        <ItemsList>
          <ItemTitle>Total (excl. fees)</ItemTitle>
          <ItemData>
            {dollarsWithCentsWithSymbol((booking?.boat_price || 0) + (booking?.special_offer_price || 0))}
          </ItemData>
        </ItemsList>
        <ExtraPaddingItem>
          <ItemTitle>
            You earn {!smallScreen && <BreakDown onClick={openPriceBreakdownModal}>Price breakdown</BreakDown>}
          </ItemTitle>
          <GreenValue>
            {dollarsWithCentsWithSymbol(ownerEarnings(booking?.state, booking?.owner_earning, booking?.owner_payout))}
          </GreenValue>
        </ExtraPaddingItem>
        {smallScreen && (
          <MobileBreakdown onClick={openPriceBreakdownModal}>
            <span>Price breakdown</span>
          </MobileBreakdown>
        )}
      </Section>
      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(approve)}>
          <Section>
            <SectionHeading>Pickup instructions</SectionHeading>
            <Textarea
              hasValue={!!watch('pickup_instructions')}
              inputRef={register({ required: 'Please provide pickup instructions' })}
              name="pickup_instructions"
              hasError={errors.pickup_instructions}
              label="Instructions"
              placeholder="Specify what the renter should expect the day of the booking (e.g. where to meet, where to park and how to access the boat)."
            />
            <ErrorMessage error={errors.pickup_instructions} />
          </Section>
          <Agree>
            <div>I have read, understand and agree to:</div>
            <Checkboxes>
              {booking?.insurance_type === 'p2p' && (
                <Checkbox
                  ownerSecondary
                  inputRef={register({ required: 'Insurance Policy' })}
                  name="p2p"
                  id="p2p"
                  label={
                    <a href={booking?.insurance_policy_url} target="_blank" rel="noreferrer">
                      Insurance Policy
                    </a>
                  }
                />
              )}
              <Checkbox
                ownerSecondary
                name="terms"
                id="terms"
                inputRef={register({ required: 'Terms of Service and Charter Party Agreement' })}
                label={
                  <span>
                    <a href="/terms" target="_blank">
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a href="/legal/time_charter_party_agreement" target="_blank">
                      Charter Party Agreement
                    </a>
                  </span>
                }
              />
              <Checkbox
                ownerSecondary
                name="uscg"
                id="uscg"
                inputRef={register({ required: 'USCG regulations' })}
                label={
                  <span>
                    <a target="_blank" href="/resources/PaxForHire.pdf">
                      USCG regulations
                    </a>{' '}
                    for this bareboat/captained charter
                  </span>
                }
              />
              {getError() && <ErrorMessage error={{ message: getError() }} />}
            </Checkboxes>
          </Agree>

          <ButtonsWrapper>
            <Button disabled={preApproveBookingMutation.isLoading} type="submit" text="pre-approve" variant="yellow" />
            <Button
              disabled={preApproveBookingMutation.isLoading}
              text="cancel"
              variant="owner"
              outline
              onClick={closeModal}
            />
          </ButtonsWrapper>
        </Form>
      </FormProvider>

      {priceBreakdownModal}
    </Wrapper>
  );
};

const BreakDown = styled.span`
  color: #0e7873;
  cursor: pointer;
  font-weight: 500;
  font-size: 12px;
  padding-left: 10px;
`;

const MobileBreakdown = styled(BreakDown)`
  width: 100%;
  display: flex;
  span {
    margin-left: auto;
  }
`;

const Form = styled.form`
  [class*='ErrorMessage'] {
    margin-bottom: 0;
  }
`;

const Checkboxes = styled.div`
  & > div {
    padding-top: 12px;
  }

  span {
    color: #5e696a !important;
  }

  label {
    cursor: pointer;
  }

  a {
    color: #0e7873 !important;
    font-size: 14px;
  }
`;

const ButtonsWrapper = styled.div`
  display: flex;
  padding-top: 25px;
  flex-flow: column;
  gap: 20px;
`;

const Agree = styled.div`
  padding-top: 15px;
  font-weight: 500;
  font-size: 14px;
  color: #5e696a !important;
`;

const Section = styled.div`
  border-top: 1px solid #dbdfe5;
  padding-top: 25px;
  margin-top: 25px;

  textarea {
    resize: none;
    min-height: 105px;
  }
`;

const SectionHeading = styled.div`
  font-weight: bold;
  padding-bottom: 10px;
  font-size: 16px;
`;

const ItemsList = styled.dl`
  display: grid;
  grid-template-columns: 1fr auto;
`;

const ItemTitle = styled.dt`
  font-weight: 500;
  margin-bottom: 5px;
`;

const ItemData = styled.dd`
  text-align: end;
  font-weight: 600;
`;

const ExtraPaddingItem = styled(ItemsList)`
  padding-bottom: 5px;
  padding-top: 15px;
`;

const GreenValue = styled(ItemData)`
  color: #2a8500;
  font-weight: 700;
`;

const Wrapper = styled.div`
  ${mediaMSmMax} {
    font-size: 14px;
  }
`;

const Heading = styled.h1`
  font-weight: bold;
  font-size: 24px;
  margin-bottom: 10px;
  ${mediaMSmMax} {
    display: none;
  }
`;

export default PreApproveModal;
